import React, { useMemo, useState } from 'react';

import type { LinkShareTemplateData } from '~/components/communications/LinkShare';
import LinkShare from '~/components/communications/LinkShare';
import type {
  VideoCallCommunication,
  VideoCallSpecificBodyCommunicationState,
} from '~/components/communications/types';
import PostVideoCommunicationCard from '~/components/communications/VideoCommunication/PostVideoCommunicationCard';
import VideoCallProviderSpecificDialog from '~/components/communications/VideoCommunication/VideoCallProviderSpecificDialog';
import { contactPopulate } from '~/components/Contacts/ContactUtils';
import Button from '~/components/core/Atomic/Buttons/Button';
import LoadingSwitch from '~/components/core/Loading/LoadingSwitch';
import { GENERIC_TEMPLATES_ROUTES } from '~/components/GenericTemplates/utils/routes';
import { useCms } from '~/components/hooks/useCms';
import useFetchContact from '~/components/hooks/useFetchContact';
import useDataFetcher from '~/components/useDataFetcher';
import { reportErrorInProductionOrThrow } from '~/Utils';

interface Props {
  // contactId is required to make sure there is a contact on the communication, because according to model it can be null
  contactId: number;
  communication: VideoCallCommunication;
  communicationState: VideoCallSpecificBodyCommunicationState;
  showActionButtons: boolean;
  onCancelMeeting?: () => Promise<void>;
}
const JoinVideoCallProviderSpecificDetails: React.FC<Props> = ({
  communication,
  contactId,
  communicationState,
  showActionButtons,
  onCancelMeeting,
}) => {
  const { userOrganization } = useCms();
  // Only a ready or ongoing call offers the invite, so no other state loads the contact it is sent to.
  const isInviteShareable = communicationState === 'ready' || communicationState === 'ongoing';
  const {
    isLoading: isLoadingContact,
    isError: isErrorContact,
    data: fetchedContact,
  } = useFetchContact(isInviteShareable ? contactId : null);
  // The organization's contact carries no primary_email, and the email composer sends to it. Memoized: the composers
  // list the contact in their hook dependencies.
  const contact = useMemo(() => (fetchedContact ? contactPopulate(fetchedContact) : undefined), [fetchedContact]);
  const {
    data: templateData,
    isLoading: isLoadingTemplateData,
    isError: isErrorTemplateData,
  } = useDataFetcher<LinkShareTemplateData>(
    GENERIC_TEMPLATES_ROUTES.GENERATE_VIDEO_INVITATION({
      organizationId: userOrganization?.id,
      claimId: communication.claim_id,
      communicationId: communication.id,
    })
  );
  const [isPostVideoDialogOpen, setIsPostVideoDialogOpen] = useState<boolean>(false);
  const [isVideoDialogOpen, setIsVideoDialogOpen] = useState<boolean>(false);
  const [isCancellingCall, setIsCancellingCall] = useState(false);

  if (communication.provider === 'Dyte') {
    if (isInviteShareable) {
      const href = communication.provider_specific_details?.guest_link;

      if (!href) {
        reportErrorInProductionOrThrow(`No guest link for Dyte video communication ${communication.id}`);
        return null;
      }

      return (
        <LoadingSwitch
          isLoading={isLoadingContact || isLoadingTemplateData}
          isError={isErrorContact || isErrorTemplateData}
        >
          <div className="mt-32">
            A link was created for a new video call. You can copy this link or send it to the contact you want to meet
            with.{' '}
            {contact && templateData && (
              <LinkShare
                title="Guest Link"
                href={href}
                contact={contact}
                templateData={templateData}
                claimId={communication.claim_id}
              />
            )}
            {showActionButtons ? (
              <div className="my-20 flex items-center justify-between">
                {onCancelMeeting && communicationState === 'ready' ? (
                  <Button
                    color="secondary"
                    onClick={async () => {
                      try {
                        setIsCancellingCall(true);
                        await onCancelMeeting();
                      } catch {
                        // Do Nothing
                      }
                      setIsCancellingCall(false);
                    }}
                    disabled={isCancellingCall}
                  >
                    Cancel Meeting
                  </Button>
                ) : null}
                <Button
                  onClick={() => {
                    setIsVideoDialogOpen(true);
                  }}
                  color="primary"
                  variant="contained"
                  className="ml-auto"
                >
                  {communicationState === 'ongoing' ? 'Rejoin Call' : 'Start Call'}
                </Button>
              </div>
            ) : null}
          </div>
          {isVideoDialogOpen ? (
            <VideoCallProviderSpecificDialog
              provider={communication.provider}
              communication={communication}
              onClose={() => {
                setIsVideoDialogOpen(false);
                setIsPostVideoDialogOpen(true);
              }}
            />
          ) : null}

          {isPostVideoDialogOpen ? (
            <PostVideoCommunicationCard
              cardTitle="Video Call"
              communication={communication}
              onClose={() => {
                setIsPostVideoDialogOpen(false);
              }}
            />
          ) : null}
        </LoadingSwitch>
      );
    }
    return null;
  } else {
    reportErrorInProductionOrThrow(
      `Unsupported video provider ${communication.provider} provider to JoinVideoCallProviderSpecificDetails`
    );
    return null;
  }
};

export default JoinVideoCallProviderSpecificDetails;
